import { useCallback, useEffect, useState } from 'react';
import { collection, getDocs, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';

// The `projects/{id}/videos` subcollection is the footage list for multi-video
// projects — one doc per uploaded clip, ordered by its `order` field.
//
// RECONCILIATION WITH THE PHASE-0 MIRROR (#320): the container-v2 lazy mirror
// (hooks/useContainerMirror.js) writes ONE deterministic doc, id `main`,
// projecting the legacy single `project.video` field into this same
// subcollection. That doc is NOT footage — it is a copy of a field that is
// still authoritative elsewhere, and counting it would make every legacy
// project look like it has one footage item.
//
// The rule is therefore: the mirror doc keeps its reserved id `main` and is
// excluded here by id; footage items get content-addressed ids
// (`${seconds}-${size}-${name}`) which can never be the literal string `main`.
// Nothing in Phase 1 writes or deletes the mirror. Both are also distinguished
// by `source` (`legacy-mirror` vs `multi-video-upload`) as a second, non-load
// -bearing signal for anyone reading raw documents.
export const LEGACY_MIRROR_VIDEO_ID = 'main';
export const FOOTAGE_SOURCE = 'multi-video-upload';

export function isFootageDoc(id, data) {
  if (id === LEGACY_MIRROR_VIDEO_ID) return false;
  return (data?.source ?? FOOTAGE_SOURCE) !== 'legacy-mirror';
}

// Live footage list. Subscribed (not one-shot) because the server trigger fills
// in probe results — duration, resolution, poster — seconds after the upload
// lands, and the tab has to show them arriving. Bounded by the 100-file
// per-project cap, so this is never a big listener.
export default function useProjectVideos(projectId) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(!!projectId);

  useEffect(() => {
    if (!projectId) {
      setVideos([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const col = collection(db, 'projects', projectId, 'videos');
    const unsub = onSnapshot(query(col, orderBy('order', 'asc')), (snap) => {
      const rows = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        if (isFootageDoc(docSnap.id, data)) rows.push({ id: docSnap.id, ...data });
      });
      setVideos(rows);
      setLoading(false);
    }, (err) => {
      console.error('useProjectVideos: read error', err);
      setVideos([]);
      setLoading(false);
    });
    return () => unsub();
  }, [projectId]);

  return { videos, loading };
}

// One-shot read of the footage doc IDs already in a project — the dedup set.
// Never a listener: the preflight needs a snapshot, not a subscription.
export async function fetchExistingVideoIds(projectId, {
  getDocsFn = getDocs,
  collectionFn = collection,
} = {}) {
  if (!projectId) return new Set();
  const col = collectionFn(db, 'projects', projectId, 'videos');
  const snap = await getDocsFn(col);
  const ids = new Set();
  snap.forEach((docSnap) => {
    if (isFootageDoc(docSnap.id, docSnap.data())) ids.add(docSnap.id);
  });
  return ids;
}

// Convenience for surfaces that only need the count (the file-count cap).
export function useProjectVideoCount(projectId) {
  const { videos, loading } = useProjectVideos(projectId);
  const count = videos.length;
  const totalBytes = videos.reduce((a, v) => a + (v.sizeBytes || 0), 0);
  const reload = useCallback(() => {}, []);
  return { count, totalBytes, loading, reload };
}
